import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../services';
import { Property } from './types';
import { Tag } from '../../Tags/types';
import { AxiosResponse } from 'axios';

export const useProperties = (propertyIds: number[] = [], loadAll = true) => useQuery<Property[]>({
  queryKey: ['properties', propertyIds],
  queryFn: () => api.get(`properties`).then((res) => res.data),
  select: (data) => {
    if (propertyIds.length === 0) {
      return loadAll ? data : [];
    }
    const propertyMap = new Map(data.map(property => [property.id, property]));

    return propertyIds
      .map(id => propertyMap.get(id))
      .filter((property): property is Property => property !== undefined);
  },
});


export const useUpdateProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (property: Property) => api.put(`properties/${property.id}`, property),
    onSuccess: (response: AxiosResponse<Property>) => {
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
      void queryClient.invalidateQueries({ queryKey: ['properties', response.data.id] });
      queryClient.setQueryData(['properties', response.data.id], response.data);
    },
  });
};

export const useCreateProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (property: Property) => api.post(`properties`, property),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
};

export const usePropertyTags = () => useQuery<Tag[]>({
  queryKey: ['propertyTags'],
  queryFn: () => api.get(`tags/type/property`).then((res) => res.data),
});
